/**
 * TwoPlayerTeams Component - Single player team display
 * Renders inline (not fixed), used by DraftScreen on left/right sides.
 */

import { useApp } from '../context/AppContext';
import { getTypeColor } from '../utils/typeUtils';
import type { Pokemon } from '../types/pokemon';

interface TwoPlayerTeamsProps {
  player: 1 | 2;
}

export function TwoPlayerTeams({ player }: TwoPlayerTeamsProps) {
  const { state, dispatch } = useApp();
  const team = state.teams[player === 1 ? 0 : 1].pokemon;
  const isActive = state.currentPlayer === player;

  const bgColor = player === 1 ? 'from-red-600/90 to-red-800/90' : 'from-blue-600/90 to-blue-800/90';
  const borderColor = player === 1 ? 'border-red-500/50' : 'border-blue-500/50';
  const glowColor = player === 1 ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.4)';

  const handleClear = () => {
    if (confirm(`Vider l'equipe du Joueur ${player} ?`)) {
      dispatch({ type: "CLEAR_TEAM", player });
    }
  };

  return (
    <div
      className={`bg-gradient-to-b ${bgColor} ${borderColor} border-2 rounded-xl p-4 w-full transition-all duration-300 ${isActive ? 'ring-2 ring-yellow-400/80 scale-[1.02]' : 'opacity-60'}`}
      style={{
        backdropFilter: 'blur(10px)',
        boxShadow: isActive ? `0 0 20px ${glowColor}, 0 4px 15px rgba(0,0,0,0.3)` : '0 4px 15px rgba(0,0,0,0.3)',
      }}
    >
      {/* Header */}
      <div className="text-center mb-2">
        <h3 className="text-white font-retro text-xs uppercase tracking-wider"
          style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}>
          {state.mode === "solo" && player === 2 ? "IA" : `J${player}`}
        </h3>
        <div className="text-yellow-300/80 font-retro text-[10px]">
          {team.length}/6
        </div>
      </div>

      {/* Team slots - 2 columns */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[...Array(6)].map((_, i) => (
          <TeamSlot
            key={i}
            pokemon={team[i]}
            onRemove={team[i] ? () => dispatch({ type: "REMOVE_POKEMON", pokemonId: team[i].id, player }) : undefined}
          />
        ))}
      </div>

      {/* Clear button */}
      {team.length > 0 && (
        <button
          onClick={handleClear}
          className="w-full font-retro text-[8px] py-1 rounded bg-black/30 text-white/50 hover:text-white hover:bg-black/50 transition-all"
        >
          VIDER
        </button>
      )}
    </div>
  );
}

interface TeamSlotProps {
  pokemon?: Pokemon;
  onRemove?: () => void;
}

function TeamSlot({ pokemon, onRemove }: TeamSlotProps) {
  if (!pokemon) {
    return (
      <div className="pixel-slot w-full aspect-square bg-white/10 rounded-lg flex items-center justify-center">
        <span className="text-white/20 text-lg">?</span>
      </div>
    );
  }

  const pixelSpriteUrl = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.id}.png`;

  return (
    <div
      className="pixel-slot w-full aspect-square bg-white/90 rounded-lg p-0.5 transition-transform hover:scale-110 cursor-pointer relative overflow-hidden group"
      title={pokemon.nameFr}
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{ background: `linear-gradient(135deg, ${getTypeColor(pokemon.types[0])}, transparent)` }}
      />

      <img
        src={pixelSpriteUrl}
        alt={pokemon.nameFr}
        className="w-full h-full object-contain relative z-10"
        style={{ imageRendering: 'pixelated' }}
        onError={(e) => { e.currentTarget.src = pokemon.sprite; }}
      />

      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-[8px] font-bold opacity-0 group-hover:opacity-100 transition-opacity z-20"
        >
          x
        </button>
      )}

      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 bg-gray-900 text-white text-[8px] font-retro rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
        {pokemon.nameFr}
      </div>
    </div>
  );
}
